<template>
  <a-drawer
    :title="organization.name || '组织档案'"
    placement="right"
    :width="drawerWidth"
    :visible="visible"
    @close="handleClose"
  >
    <a-spin :spinning="loading">
      <a-tabs v-model="activeTab" @change="handleTabChange">
        <!-- Basic Info Tab -->
        <a-tab-pane key="info" tab="基本信息">
          <div class="info-section">
            <!-- Key Info Card -->
            <div class="info-card highlight-card">
              <div class="info-card-title">关键信息</div>
              <div class="info-card-content">
                <div class="info-row">
                  <span class="info-label">组织名称</span>
                  <span class="info-value primary">{{ organization.name || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">状态</span>
                  <span class="info-value">
                    <a-tag :color="getStatusColor(organization.status)" size="large">
                      {{ getStatusText(organization.status) }}
                    </a-tag>
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">订阅到期</span>
                  <span class="info-value" :class="{ 'expired-warning': isExpired }">
                    {{ formatDateOnly(organization.subscription_expired_at) || '未订阅' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Database Info Card -->
            <div class="info-card">
              <div class="info-card-title">数据库配置</div>
              <div class="info-card-content">
                <div class="info-row">
                  <span class="info-label">数据库标识</span>
                  <span class="info-value code">{{ organization.db_identifier || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">数据库名称</span>
                  <span class="info-value code">{{ organization.db_name || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Permissions Card -->
            <div class="info-card">
              <div class="info-card-title">可用权限</div>
              <div class="info-card-content">
                <div v-if="organization.permissions && organization.permissions.length > 0" class="permission-tags">
                  <a-tag v-for="perm in organization.permissions" :key="perm" color="blue">
                    {{ perm }}
                  </a-tag>
                </div>
                <span v-else class="empty-text">暂无权限配置</span>
              </div>
            </div>

            <!-- Timestamps Card -->
            <div class="info-card">
              <div class="info-card-title">时间信息</div>
              <div class="info-card-content">
                <div class="info-row">
                  <span class="info-label">创建时间</span>
                  <span class="info-value">{{ formatDate(organization.created_at) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">更新时间</span>
                  <span class="info-value">{{ formatDate(organization.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>

        <!-- Users Tab -->
        <a-tab-pane key="users" tab="用户列表">
          <div class="tab-header">
            <a-button type="primary" @click="openCreateUserDialog">
              <a-icon type="plus" />新增用户
            </a-button>
          </div>
          <a-table
            :columns="userColumns"
            :data-source="users"
            :loading="usersLoading"
            :pagination="usersPagination"
            rowKey="id"
            @change="handleUsersTableChange"
          >
            <span slot="role" slot-scope="role">
              <a-tag>{{ role && role.name || '未分配' }}</a-tag>
            </span>
            <span slot="created_at" slot-scope="text">
              {{ formatDate(text) }}
            </span>
            <span slot="actions" slot-scope="text, record">
              <a-button type="link" size="small" @click="openEditUserDialog(record)">
                编辑
              </a-button>
            </span>
          </a-table>
        </a-tab-pane>

        <!-- Farms Tab -->
        <a-tab-pane key="farms" tab="牧场列表">
          <a-table
            :columns="farmColumns"
            :data-source="farms"
            :loading="farmsLoading"
            :pagination="farmsPagination"
            rowKey="id"
            @change="handleFarmsTableChange"
          >
            <span slot="created_at" slot-scope="text">
              {{ formatDate(text) }}
            </span>
          </a-table>
        </a-tab-pane>

        <!-- Subscriptions Tab -->
        <a-tab-pane key="subscriptions" tab="订阅记录">
          <div class="tab-header">
            <a-button type="primary" @click="openCreateSubscriptionDialog">
              <a-icon type="plus" />新增订阅
            </a-button>
          </div>
          <a-table
            :columns="subscriptionColumns"
            :data-source="subscriptions"
            :loading="subscriptionsLoading"
            :pagination="subscriptionsPagination"
            rowKey="id"
            @change="handleSubscriptionsTableChange"
          >
            <span slot="status" slot-scope="status, record">
              <a-tag :color="getSubscriptionStatusColor(status)">
                {{ record.status_text || getSubscriptionStatusText(status) }}
              </a-tag>
            </span>
            <span slot="started_at" slot-scope="text">
              {{ formatDateOnly(text) }}
            </span>
            <span slot="expired_at" slot-scope="text">
              {{ formatDateOnly(text) }}
            </span>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </a-spin>

    <!-- Create User Dialog -->
    <a-modal
      :title="userDialogTitle"
      :visible="createUserDialogVisible"
      @ok="handleSaveUser"
      @cancel="createUserDialogVisible = false"
      :confirmLoading="createUserLoading"
      width="500px"
    >
      <a-form :form="userForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="用户名" required>
          <a-input
            v-decorator="['user_name', { rules: [{ required: true, message: '请输入用户名' }] }]"
            placeholder="请输入用户名"
          />
        </a-form-item>
        <a-form-item label="密码" v-if="!editingUser" required>
          <a-input-password
            v-decorator="['user_password', { rules: [
              { required: true, message: '请输入密码' },
              { pattern: /^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z!@#$%^&*]{6,}$/, message: '密码必须包含数字和英文，长度至少6位' }
            ] }]"
            placeholder="请输入密码"
          />
        </a-form-item>
        <a-form-item label="手机号" required>
          <a-input
            v-decorator="['mobile', { rules: [{ required: true, message: '请输入手机号' }] }]"
            placeholder="请输入手机号"
          />
        </a-form-item>
        <a-form-item label="邮箱">
          <a-input
            v-decorator="['user_mail']"
            placeholder="请输入邮箱"
          />
        </a-form-item>
        <a-form-item label="昵称">
          <a-input
            v-decorator="['nickname']"
            placeholder="请输入昵称"
          />
        </a-form-item>
        <a-form-item label="角色">
          <a-select v-decorator="['role_id']" placeholder="请选择角色">
            <a-select-option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Create Subscription Dialog -->
    <a-modal
      title="新增订阅"
      :visible="createSubscriptionDialogVisible"
      @ok="handleCreateSubscription"
      @cancel="createSubscriptionDialogVisible = false"
      :confirmLoading="createSubscriptionLoading"
      width="450px"
    >
      <a-form :form="subscriptionForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="开始时间" required>
          <a-date-picker
            v-decorator="['started_at', { 
              rules: [{ required: true, message: '请选择开始时间' }]
            }]"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="false"
          />
        </a-form-item>
        <a-form-item label="到期时间" required>
          <a-date-picker
            v-decorator="['expired_at', { 
              rules: [{ required: true, message: '请选择到期时间' }]
            }]"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="false"
          />
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea
            v-decorator="['remarks']"
            placeholder="请输入备注"
            :rows="3"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </a-drawer>
</template>

<script>
import { dateFormat } from '@/utils/dateFormat'

export default {
  name: 'OrganizationProfileDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    organization: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      activeTab: 'info',
      // Users
      users: [],
      usersLoading: false,
      usersPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `共 ${total} 条`
      },
      // Farms
      farms: [],
      farmsLoading: false,
      farmsPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `共 ${total} 条`
      },
      // Subscriptions
      subscriptions: [],
      subscriptionsLoading: false,
      subscriptionsPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `共 ${total} 条`
      },
      // Create User Dialog
      createUserDialogVisible: false,
      createUserLoading: false,
      userForm: this.$form.createForm(this),
      roles: [],
      editingUser: null, // 正在编辑的用户
      userDialogTitle: '新增用户', // 对话框标题
      // Create Subscription Dialog
      createSubscriptionDialogVisible: false,
      createSubscriptionLoading: false,
      subscriptionForm: this.$form.createForm(this),
      // Columns
      userColumns: [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '用户名', dataIndex: 'user_name' },
        { title: '昵称', dataIndex: 'nickname' },
        { title: '手机号', dataIndex: 'mobile' },
        { title: '角色', dataIndex: 'role', scopedSlots: { customRender: 'role' } },
        { title: '创建时间', dataIndex: 'created_at', width: 170, scopedSlots: { customRender: 'created_at' } },
        { title: '操作', key: 'actions', width: 100, scopedSlots: { customRender: 'actions' } }
      ],
      farmColumns: [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '牧场名称', dataIndex: 'name' },
        { title: '联系电话', dataIndex: 'phone' },
        { title: '地址', dataIndex: 'location' },
        { title: '创建时间', dataIndex: 'created_at', width: 170, scopedSlots: { customRender: 'created_at' } }
      ],
      subscriptionColumns: [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '状态', dataIndex: 'status', width: 90, scopedSlots: { customRender: 'status' } },
        { title: '开始时间', dataIndex: 'started_at', width: 170, scopedSlots: { customRender: 'started_at' } },
        { title: '到期时间', dataIndex: 'expired_at', width: 170, scopedSlots: { customRender: 'expired_at' } },
        { title: '备注', dataIndex: 'remarks' }
      ]
    }
  },
  computed: {
    drawerWidth() {
      // Calculate width: 100vw - sidebar width (typically 256px)
      return 'calc(100vw - 256px)'
    },
    isExpired() {
      if (!this.organization.subscription_expired_at) return false
      const expiredDate = new Date(this.organization.subscription_expired_at)
      return expiredDate < new Date()
    }
  },
  watch: {
    visible(val) {
      if (val && this.organization.id) {
        this.activeTab = 'info'
        this.loadData()
      }
    }
  },
  methods: {
    // Load data based on active tab
    loadData() {
      this.loadUsers()
      this.loadFarms()
      this.loadSubscriptions()
      this.loadRoles()
    },

    handleTabChange(key) {
      // Data is already loaded in loadData()
    },

    // Users
    async loadUsers() {
      if (!this.organization.id) return
      this.usersLoading = true
      try {
        const response = await this.$api.get_users_by_tenant(this.organization.id, {
          page_no: this.usersPagination.current,
          page_size: this.usersPagination.pageSize
        })
        this.users = response.data || []
        this.usersPagination.total = response.page && response.page.total || 0
      } catch (error) {
        console.error('加载用户列表失败:', error)
        this.$message.error('加载用户列表失败')
      } finally {
        this.usersLoading = false
      }
    },

    handleUsersTableChange(pagination) {
      this.usersPagination.current = pagination.current
      this.usersPagination.pageSize = pagination.pageSize
      this.loadUsers()
    },

    // Farms
    async loadFarms() {
      if (!this.organization.id) return
      this.farmsLoading = true
      try {
        const response = await this.$api.get_tenant_farms(this.organization.id, {
          page_no: this.farmsPagination.current,
          page_size: this.farmsPagination.pageSize
        })
        this.farms = response.data && response.data.list || []
        this.farmsPagination.total = response.data && response.data.page && response.data.page.total || 0
      } catch (error) {
        console.error('加载牧场列表失败:', error)
        this.$message.error('加载牧场列表失败')
      } finally {
        this.farmsLoading = false
      }
    },

    handleFarmsTableChange(pagination) {
      this.farmsPagination.current = pagination.current
      this.farmsPagination.pageSize = pagination.pageSize
      this.loadFarms()
    },

    // Subscriptions
    async loadSubscriptions() {
      if (!this.organization.id) return
      this.subscriptionsLoading = true
      try {
        const response = await this.$api.get_subscriptions_by_tenant(this.organization.id, {
          page_no: this.subscriptionsPagination.current,
          page_size: this.subscriptionsPagination.pageSize
        })
        this.subscriptions = response.data || []
        this.subscriptionsPagination.total = response.page && response.page.total || 0
      } catch (error) {
        console.error('加载订阅列表失败:', error)
        this.$message.error('加载订阅列表失败')
      } finally {
        this.subscriptionsLoading = false
      }
    },

    handleSubscriptionsTableChange(pagination) {
      this.subscriptionsPagination.current = pagination.current
      this.subscriptionsPagination.pageSize = pagination.pageSize
      this.loadSubscriptions()
    },

    // Roles
    async loadRoles() {
      try {
        const response = await this.$api.get_roles({ page_size: 100 })
        this.roles = response.data && response.data.list || response.data || []
      } catch (error) {
        console.error('加载角色列表失败:', error)
      }
    },

    // Create User
    openCreateUserDialog() {
      this.editingUser = null
      this.userDialogTitle = '新增用户'
      this.userForm.resetFields()
      this.createUserDialogVisible = true
    },

    // Edit User
    openEditUserDialog(record) {
      this.editingUser = record
      this.userDialogTitle = '编辑用户'
      this.userForm.resetFields()
      this.$nextTick(() => {
        this.userForm.setFieldsValue({
          user_name: record.user_name,
          mobile: record.mobile,
          user_mail: record.user_mail,
          nickname: record.nickname,
          role_id: record.role?.id || record.role_id
        })
      })
      this.createUserDialogVisible = true
    },

    async handleSaveUser() {
      try {
        const values = await this.userForm.validateFields()
        this.createUserLoading = true
        
        if (this.editingUser) {
          // 编辑用户
          const updateData = {
            user_name: values.user_name,
            mobile: values.mobile,
            user_mail: values.user_mail,
            nickname: values.nickname,
            role_id: values.role_id
          }
          await this.$api.updateTenantUser(this.editingUser.id, updateData)
          this.$message.success('更新用户成功')
        } else {
          // 新增用户
          await this.$api.create_tenant_user_for_tenant({
            ...values,
            tenant_id: this.organization.id
          })
          this.$message.success('创建用户成功')
        }
        
        this.createUserDialogVisible = false
        this.loadUsers()
        
        // Emit event to refresh parent
        this.$emit('refresh')
      } catch (error) {
        if (error.errorFields) return
        console.error('保存用户失败:', error)
        this.$message.error(error.message || '保存用户失败')
      } finally {
        this.createUserLoading = false
      }
    },

    async handleCreateUser() {
      // 保留原方法名兼容，调用新方法
      await this.handleSaveUser()
    },

    // Create Subscription
    openCreateSubscriptionDialog() {
      this.subscriptionForm.resetFields()
      // Set default dates using native Date
      this.$nextTick(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const oneYearLater = new Date()
        oneYearLater.setFullYear(oneYearLater.getFullYear() + 1)
        // Set expired_at to 00:00:00 of the selected day
        oneYearLater.setHours(0, 0, 0, 0)
        
        this.subscriptionForm.setFieldsValue({
          started_at: today,
          expired_at: oneYearLater
        })
      })
      this.createSubscriptionDialogVisible = true
    },

    async handleCreateSubscription() {
      try {
        const values = await this.subscriptionForm.validateFields()
        this.createSubscriptionLoading = true
        
        // values.started_at and values.expired_at are Date objects
        // Format dates as YYYY-MM-DD strings (no time component)
        const startedAt = values.started_at
        const expiredAt = values.expired_at
        
        // Helper function to format date as YYYY-MM-DD
        const formatDateToYMD = (date) => {
          if (!date) return null
          const d = date instanceof Date ? date : new Date(date)
          const year = d.getFullYear()
          const month = String(d.getMonth() + 1).padStart(2, '0')
          const day = String(d.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }
        
        const data = {
          tenant_id: this.organization.id,
          service_name: '基础服务',
          started_at: formatDateToYMD(startedAt),
          expired_at: formatDateToYMD(expiredAt),
          remarks: values.remarks
        }
        
        await this.$api.create_subscription(data)
        
        this.$message.success('创建订阅成功，租户到期时间已更新')
        this.createSubscriptionDialogVisible = false
        this.loadSubscriptions()
        
        // Emit event to refresh parent (to update subscription_expired_at)
        this.$emit('refresh')
      } catch (error) {
        if (error.errorFields) return
        console.error('创建订阅失败:', error)
        this.$message.error(error.message || '创建订阅失败')
      } finally {
        this.createSubscriptionLoading = false
      }
    },

    // Utils
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return dateFormat(d, 'yyyy-MM-dd hh:mm:ss')
    },

    // Format date only (no time) - for subscription dates
    formatDateOnly(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return dateFormat(d, 'yyyy-MM-dd')
    },

    getStatusColor(status) {
      const colors = {
        1: 'orange',   // UNSUBSCRIBED
        2: 'green'     // NORMAL
      }
      return colors[status] || 'default'
    },

    getStatusText(status) {
      const texts = {
        1: '未订阅',
        2: '正常'
      }
      return texts[status] || '未知'
    },

    getSubscriptionStatusColor(status) {
      const colors = {
        1: 'green',   // ACTIVE
        2: 'red',     // EXPIRED
        3: 'default'  // CANCELLED
      }
      return colors[status] || 'default'
    },

    getSubscriptionStatusText(status) {
      const texts = {
        1: '生效中',
        2: '已过期',
        3: '已取消'
      }
      return texts[status] || '未知'
    },

    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.expired-warning {
  color: #ff4d4f;
  font-weight: bold;
}

.tab-header {
  margin-bottom: 16px;
}

// Info Section Styles
.info-section {
  padding: 8px 0;
}

.info-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
  
  &.highlight-card {
    background: linear-gradient(135deg, #f6f8fc 0%, #fff 100%);
    border: 1px solid #e6f7ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.08);
  }
}

.info-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-row {
  display: flex;
  align-items: center;
  line-height: 1.6;
}

.info-label {
  flex-shrink: 0;
  width: 100px;
  color: #666;
  font-size: 14px;
}

.info-value {
  flex: 1;
  color: #333;
  font-size: 14px;
  
  &.primary {
    font-size: 16px;
    font-weight: 600;
    color: #1890ff;
  }
  
  &.code {
    font-family: 'Monaco', 'Menlo', monospace;
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 13px;
  }
}

.permission-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.empty-text {
  color: #999;
  font-size: 14px;
}

// Drawer Styles
::v-deep .ant-drawer-body {
  padding: 24px;
}

::v-deep .ant-tabs-nav {
  margin-bottom: 20px;
}

// Table Styles
::v-deep .ant-table {
  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: 500;
    padding: 14px 16px;
  }
  
  .ant-table-tbody > tr > td {
    padding: 14px 16px;
  }
}

// Pagination Styles
::v-deep .ant-pagination {
  margin-top: 16px;
  padding: 12px 0;
  text-align: right;
}

// Form Styles
::v-deep .ant-form-item {
  margin-bottom: 20px;
}

// Date Picker Styles
::v-deep .ant-calendar-picker {
  width: 100%;
}
</style>
